import { useQueryClient } from "@tanstack/react-query";
import List from "devextreme-react/list";
import React, { useState } from "react";
import { Image } from "../../../../components/image/index";
import { getDDMMYYYFormat, getHHmmFormat, getUCTDate } from "../../../../helper/dateTimeHelper";
import { Icon } from "../../../../modules/icon";
import { ObjectUtils } from "../../../../modules/utils/object.utils";
import { BusinessTicketRequestService } from "../../../../services/company-office/BusinessTicketService";
import { ExternalSecurityControlCardRequestService } from "../../../../services/company-office/ExternalSecurityControlCardService";
import { MeetingRequestService } from "../../../../services/company-office/MeetingRequest.service";
import { SecurityControlCardRequestService } from "../../../../services/company-office/SecurityControlCardService";
import { StationeryRequestService } from "../../../../services/company-office/StationeryRequest.service";
import { TravelCardRequestService } from "../../../../services/company-office/TravelCardRequest";
import { VehicleRequestService } from "../../../../services/company-office/VehicleRequestService";
import { EprCodeProcessService } from "../../../../services/eprcode-process/eprcode-process.service";
import { HOChangePersonRequestService } from "../../../../services/human-organization/change-person-request.service";
import { HOSummaryEmployeeRequestService as HOSummaryEmployeeRequestService1 } from "../../../../services/human-organization/summary-employee-request.service";
import { HOSummaryEmployeeRequestService as HOSummaryEmployeeRequestService2 } from "../../../../services/human-organization/summary-register-request.service";
import { ITCodeRequestService } from "../../../../services/it-code-request/itCodeRequest.service";
import { ITDeleteAccountRequestService } from "../../../../services/it-delete-account/itDeleteAccountRequest.service";
import { ITRepairInfoService } from "../../../../services/it-repair/itRepairInfo.service";
import { ITToolRequestService } from "../../../../services/it-tools-request/itToolRequest.service";
import { PlanningDepartmentExportService } from "../../../../services/planning-department/export-warehouse";
import { PlanningDepartmentRecallToolService } from "../../../../services/planning-department/recall-tool";
import styles from "./style.module.scss";

interface Comment {
  id: number;
  author: string;
  body: string;
  email: string;
  avatar?: string;
  like?: any;
  comment?: any;
  name?: string;
}

interface CommentSectionProps {
  comments: Comment[];
  data: any;
  user: any;
  id: number;
  className: string;
  refetch?: () => void;
}

const CommentSection = ({ data, user, id, className, refetch }: CommentSectionProps) => {
  const avatar = ObjectUtils.getIn(user?.current, "idTokenClaims.avatar");
  const [showComments, setShowComments] = useState(false);
  const [currentComment, setCurrentComment] = useState(null as any);
  const comments = data?.fields?.eComments ? JSON.parse(data?.fields?.eComments) : [];
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);

  const handleAddComment = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
  };

  const handleKeyPress = (event: any) => {
    if (event.key === "Enter") {
      handleSubmit();
    }
  };

  const handleSubmit = async () => {
    if (!currentComment || !currentComment.trim() || loading) return;

    setLoading(true);

    const today = new Date();
    const dataId: number = data.id;
    let date = getUCTDate(today);

    const currentUser = {
      id: user.current.localAccountId,
      name: user.current.name,
      email: user.current.username,
    };
    const comment = {
      user: { ...currentUser },
      comment: currentComment,
      time: date,
      like: 0,
      reply: null,
    };
    comments.push(comment);

    const fields = { eComments: JSON.stringify(comments) };
    switch (id) {
      case 1:
        await EprCodeProcessService.updateEPRCodeRequest(dataId, fields);
        queryClient.invalidateQueries({ queryKey: ["epr-code-requests"] });
        break;
      case 2:
        await ITCodeRequestService.updateITCodeRequest(dataId, fields);
        queryClient.invalidateQueries({ queryKey: ["code-process-requests"] });
        break;
      case 3:
        await ITRepairInfoService.updateITRepairInfo(dataId, fields);
        queryClient.invalidateQueries({ queryKey: ["repair-tools-requests"] });
        break;
      case 4:
        await ITDeleteAccountRequestService.updateITDeleteAccountRequest(dataId, fields);
        queryClient.invalidateQueries({ queryKey: ["delete-account-requests"] });
        break;
      case 5:
        await ITToolRequestService.updateITToolRequest(dataId, fields);
        queryClient.invalidateQueries({ queryKey: ["demand-for-supplies-requests"] });
        break;
      case 6:
        await VehicleRequestService.updateVehicleRequest(dataId, fields);
        break;
      case 7:
        await BusinessTicketRequestService.updateBusinessTicketRequest(dataId, fields);
        refetch?.();
        break;
      case 8:
        await SecurityControlCardRequestService.updateSecurityControlCardRequest(dataId, fields);
        break;
      case 9:
        await ExternalSecurityControlCardRequestService.updateExternalSecurityControlCardRequest(dataId, fields);
        break;
      case 10:
        await TravelCardRequestService.updateTravelCardRequest(dataId, fields);
        break;
      case 11:
        await PlanningDepartmentRecallToolService.updateRequest(dataId, fields);
        break;
      case 13:
        await PlanningDepartmentExportService.updateRequest(dataId, fields);
        break;
      case 14:
        await HOSummaryEmployeeRequestService1.updateSummaryEmployeeRequest(dataId, fields);
        break;
      case 15:
        await HOChangePersonRequestService.updateChangePersonRequest(dataId, fields);
        break;
      case 16:
        await MeetingRequestService.updateMeetingRequest(dataId, fields);
        break;

      case 17:
        await HOSummaryEmployeeRequestService2.updateSummaryEmployeeRequest(dataId, fields);
        break;
      case 18:
        await StationeryRequestService.updateStationeryRequest(dataId, fields);
        break;
    }
    setCurrentComment(null);
    setLoading(false);
  };

  const handleItemRender = (comment: any) => {
    return (
      <>
        <div className="comment__contents">
          <p className="comment__header text-center flex-shrink-0">
            <Image src={avatar} type="avatar" className="user-comment__avatar" />
          </p>
          <div className="comment__body">
            <span className="comment__body-title font-weight-bold">{comment?.user?.name}</span>
            <span className="comment__body-content">{comment?.comment}</span>
            <div className="comment__time">
              <ul className="d-flex flex-row justify-content-end">
                {/* <li className="like p-2 cursor" onClick={handleClick}>Thích</li>
                  <li className="like p-2 cursor">Phản hồi</li> */}
                <li style={{ opacity: 0.6 }}>
                  {getDDMMYYYFormat(comment?.time)} : {getHHmmFormat(comment?.time)}
                </li>
              </ul>
            </div>
          </div>
        </div>
      </>
    );
  };

  return (
    <div className="comment-section">
      <p className="px-4 py-1">{comments?.length > 0 ? comments.length + " Thông tin" : "Chưa có thông tin nào"}</p>
      {comments?.length > 0 && (
        <button onClick={() => setShowComments(!showComments)} className="button__comment btn-sm">
          <Icon.ArrowDown />
          {!showComments ? "Ẩn thông tin" : "Hiện thông tin"}
        </button>
      )}
      {!showComments && (
        <div className="comment-list">
          <List
            dataSource={comments}
            itemRender={comments && handleItemRender}
            hoverStateEnabled={false}
            noDataText=" "
          ></List>
        </div>
      )}
      <form onSubmit={handleAddComment} className="mt-3">
        <div className="comment-form-fields">
          <Image src={avatar} type="avatar" className="comment__avatar" />
          <div className="comment-form-input">
            <input
              className="input__enter-a-comment"
              style={{ paddingRight: "40px" }}
              placeholder="Nhập thông tin trao đổi (nếu có)"
              onChange={(e) => setCurrentComment(e.target.value)}
              value={currentComment ? currentComment : ""}
              onKeyPress={handleKeyPress}
              required
            />
            {loading ? (
              <span>
                <span className={styles.send__loading} />
              </span>
            ) : (
              <span
                onClick={handleSubmit}
                className={currentComment && currentComment.trim() && !loading ? styles.send__active : ""}
              >
                <Icon.SendPlan1 />
              </span>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

export default CommentSection;
